import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, FileText, Share2, Loader2, Check, Target, Table2, Calculator, CalendarRange, Wand2, ImagePlus, Swords, Download,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import ShareDialog from "../components/ShareDialog";
import UpgradeDialog from "../components/UpgradeDialog";
import PremiumGate from "../components/PremiumGate";
import ArenaPanel from "../components/ArenaPanel";
import Scorecard from "../components/Scorecard";
import LogoStudio from "../components/LogoStudio";
import BrandKit from "../components/BrandKit";
import SwotBoard from "../components/SwotBoard";
import CompetitorTracker from "../components/CompetitorTracker";
import PricingCalculator from "../components/PricingCalculator";
import ActionPlan from "../components/ActionPlan";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import api from "../lib/apiClient";
import { exportPlanPDF } from "../lib/pdf";
import { downloadDataUrl, slugFilename } from "../lib/download";
import PolicyConsentDialog from "../components/PolicyConsentDialog";

const TABS = [
  { key: "swot", label: "SWOT", icon: Target },
  { key: "arena", label: "AI Arena", icon: Swords },
  { key: "competitors", label: "Competitors", icon: Table2 },
  { key: "pricing", label: "Pricing", icon: Calculator },
  { key: "action", label: "90-Day Plan", icon: CalendarRange },
];

export default function PlanEditor() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { user, acceptPolicy } = useAuth();
  const isPremium = user?.plan === "premium";
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState("saved"); // saved | saving | dirty
  const [showShare, setShowShare] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [showLogoStudio, setShowLogoStudio] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [policyLoading, setPolicyLoading] = useState(false);
  const timer = useRef(null);
  const first = useRef(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/plans/${planId}`);
        setPlan(res.data);
      } catch (e) {
        toast.error("Plan not found");
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, [planId, navigate]);

  const save = useCallback(async (data) => {
    setSaveState("saving");
    try {
      await api.put(`/plans/${planId}`, {
        business_name: data.business_name, industry: data.industry, description: data.description,
        swot: data.swot, competitors: data.competitors, pricing: data.pricing, action_plan: data.action_plan,
      });
      setSaveState("saved");
    } catch (e) {
      setSaveState("dirty");
      toast.error("Save failed");
    }
  }, [planId]);

  useEffect(() => {
    if (!plan) return;
    if (first.current) { first.current = false; return; }
    setSaveState("dirty");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => save(plan), 800);
    return () => timer.current && clearTimeout(timer.current);
  }, [plan, save]);

  const patch = (partial) => setPlan((p) => ({ ...p, ...partial }));

  const exportPdf = () => {
    if (user?.policy_accepted) { exportPlanPDF(plan, { premium: isPremium }); return; }
    setShowPolicy(true);
  };

  const onPolicyAccept = async () => {
    setPolicyLoading(true);
    try {
      await acceptPolicy();
      setShowPolicy(false);
      exportPlanPDF(plan, { premium: isPremium });
    } catch (e) {
      toast.error("Could not record your acceptance. Please try again.");
    } finally {
      setPolicyLoading(false);
    }
  };

  const fullDraft = async () => {
    setDrafting(true);
    try {
      const res = await api.post("/ai/full-draft", {
        business_name: plan.business_name, industry: plan.industry, description: plan.description,
      });
      setPlan((p) => {
        const swot = { ...p.swot };
        ["strengths", "weaknesses", "opportunities", "threats"].forEach((q) => {
          const existing = new Set((p.swot[q] || []).map((i) => i.text.toLowerCase()));
          const fresh = (res.data.swot[q] || []).filter((i) => !existing.has(i.text.toLowerCase()));
          swot[q] = [...(p.swot[q] || []), ...fresh];
        });
        const compExisting = new Set((p.competitors || []).map((c) => c.name.toLowerCase()));
        const competitors = [...(p.competitors || []), ...res.data.competitors.filter((c) => !compExisting.has(c.name.toLowerCase()))];
        const action_plan = { ...p.action_plan };
        ["phase1", "phase2", "phase3"].forEach((ph) => {
          action_plan[ph] = [...(p.action_plan[ph] || []), ...(res.data.action_plan[ph] || [])];
        });
        const pr = p.pricing || {};
        const priceEmpty = !(Number(pr.cogs) > 0 || Number(pr.volume) > 0 || Number(pr.margin) > 0);
        const pricing = priceEmpty && res.data.pricing ? res.data.pricing : pr;
        return { ...p, swot, competitors, action_plan, pricing };
      });
      toast.success("AI drafted your full plan");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI draft failed");
    } finally {
      setDrafting(false);
    }
  };

  if (loading || !plan) {
    return (
      <div className="min-h-screen grid-bg flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen grid-bg text-slate-100">
      <Navbar>
        <div className="hidden sm:flex items-center gap-1.5 text-sm text-slate-400 mr-2">
          {saveState === "saving" && <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…</>}
          {saveState === "saved" && <><Check className="h-3.5 w-3.5 text-emerald-400" /> Saved</>}
          {saveState === "dirty" && <span className="text-amber-400">Unsaved…</span>}
        </div>
        <Button data-testid="editor-full-draft-button" size="sm" onClick={fullDraft} disabled={drafting} className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white">
          {drafting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Wand2 className="h-4 w-4 mr-1" />} Draft with AI
        </Button>
        <Button data-testid="editor-pdf-button" variant="ghost" size="sm" onClick={exportPdf} className="text-slate-200 hover:bg-slate-800">
          <FileText className="h-4 w-4 mr-1" /> PDF
        </Button>
        <Button data-testid="editor-share-button" size="sm" onClick={() => setShowShare(true)} className="cta-btn">
          <Share2 className="h-4 w-4 mr-1" /> Share
        </Button>
      </Navbar>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <button data-testid="back-to-dashboard" onClick={() => navigate("/dashboard")} className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition mb-4">
          <ArrowLeft className="h-4 w-4" /> All plans
        </button>

        <div className="mb-6 flex items-start gap-4">
          <div className="shrink-0">
            {plan.logo ? (
              <div className="flex flex-col items-center gap-1.5">
                <div className="relative group">
                  <img data-testid="plan-logo" src={plan.logo} alt="logo" className="h-20 w-20 rounded-2xl object-cover border border-slate-700 bg-white" />
                  <button
                    data-testid="regenerate-logo-button"
                    onClick={() => setShowLogoStudio(true)}
                    className="absolute inset-0 grid place-items-center rounded-2xl bg-black/60 opacity-0 group-hover:opacity-100 transition text-white text-xs"
                  >
                    <ImagePlus className="h-4 w-4 mb-0.5" />Change
                  </button>
                </div>
                <button
                  data-testid="download-logo-button"
                  onClick={() => downloadDataUrl(plan.logo, slugFilename(plan.business_name, "logo"))}
                  className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-teal-300 transition"
                >
                  <Download className="h-3 w-3" /> Logo
                </button>
              </div>
            ) : (
              <button
                data-testid="generate-logo-button"
                onClick={() => setShowLogoStudio(true)}
                className="h-20 w-20 rounded-2xl border border-dashed border-slate-700 grid place-items-center text-slate-400 hover:text-orange-300 hover:border-orange-400/60 transition text-[11px] text-center px-1"
              >
                <div className="flex flex-col items-center gap-1"><ImagePlus className="h-5 w-5" />AI logo</div>
              </button>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <input
              data-testid="editor-name-input"
              value={plan.business_name}
              onChange={(e) => patch({ business_name: e.target.value })}
              className="font-display text-3xl sm:text-4xl font-extrabold bg-transparent outline-none w-full truncate focus:text-teal-300 transition"
            />
            <input
              data-testid="editor-industry-input"
              value={plan.industry || ""}
              onChange={(e) => patch({ industry: e.target.value })}
              placeholder="Add an industry / niche…"
              className="text-slate-400 bg-transparent outline-none w-full mt-1"
            />
            {plan.brand_kit?.tagline && (
              <p data-testid="editor-tagline" className="text-teal-300 italic text-sm mt-1.5">"{plan.brand_kit.tagline}"</p>
            )}
          </div>
        </div>

        {plan.brand_kit?.banner && (
          <div className="mb-6 relative group">
            <img data-testid="header-brand-banner" src={plan.brand_kit.banner} alt="brand banner" className="w-full max-h-40 object-cover rounded-2xl border border-slate-800" />
            <button
              data-testid="download-header-banner-button"
              onClick={() => downloadDataUrl(plan.brand_kit.banner, slugFilename(plan.business_name, "banner"))}
              className="absolute top-3 right-3 flex items-center gap-1 rounded-lg bg-black/60 backdrop-blur px-2.5 py-1.5 text-xs text-white opacity-0 group-hover:opacity-100 transition hover:bg-black/80"
            >
              <Download className="h-3.5 w-3.5" /> Banner
            </button>
          </div>
        )}

        <div className="mb-6">
          <Scorecard plan={plan} />
        </div>

        <div className="mb-6">
          <PremiumGate premium={isPremium} onUpgrade={() => setShowUpgrade(true)} title="Brand Kit">
            <BrandKit plan={plan} value={plan.brand_kit} onChange={(bk) => patch({ brand_kit: bk })} />
          </PremiumGate>
        </div>

        <Tabs defaultValue="swot" className="w-full">
          <TabsList className="bg-slate-900/70 border border-slate-800 p-1 h-auto flex flex-wrap gap-1">
            {TABS.map((t) => (
              <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key}`} className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-slate-300 rounded-lg px-4 py-2">
                <t.icon className="h-4 w-4 mr-1.5" /> {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="swot" className="mt-6">
            <SwotBoard plan={plan} value={plan.swot} onChange={(swot) => patch({ swot })} premium={isPremium} onUpgrade={() => setShowUpgrade(true)} />
          </TabsContent>
          <TabsContent value="arena" className="mt-6">
            <PremiumGate premium={isPremium} onUpgrade={() => setShowUpgrade(true)} title="AI Model Arena">
              <ArenaPanel plan={plan} value={plan.arena} onChange={(arena) => patch({ arena })} />
            </PremiumGate>
          </TabsContent>
          <TabsContent value="competitors" className="mt-6">
            <PremiumGate premium={isPremium} onUpgrade={() => setShowUpgrade(true)} title="Competitor matrix">
              <CompetitorTracker plan={plan} value={plan.competitors} onChange={(competitors) => patch({ competitors })} />
            </PremiumGate>
          </TabsContent>
          <TabsContent value="pricing" className="mt-6">
            <PremiumGate premium={isPremium} onUpgrade={() => setShowUpgrade(true)} title="Pricing research">
              <PricingCalculator plan={plan} value={plan.pricing} onChange={(pricing) => patch({ pricing })} />
            </PremiumGate>
          </TabsContent>
          <TabsContent value="action" className="mt-6">
            <PremiumGate premium={isPremium} onUpgrade={() => setShowUpgrade(true)} title="90-day action plan">
              <ActionPlan plan={plan} value={plan.action_plan} onChange={(action_plan) => patch({ action_plan })} />
            </PremiumGate>
          </TabsContent>
        </Tabs>
      </div>

      {showShare && (
        <ShareDialog open={showShare} onOpenChange={setShowShare} plan={plan} onShared={(tok) => patch({ share_token: tok })} />
      )}

      <LogoStudio open={showLogoStudio} onOpenChange={setShowLogoStudio} plan={plan} onSelect={(logo, brand_kit) => patch(brand_kit ? { logo, brand_kit } : { logo })} />
      <UpgradeDialog open={showUpgrade} onOpenChange={setShowUpgrade} />
      <PolicyConsentDialog open={showPolicy} onOpenChange={setShowPolicy} onAccept={onPolicyAccept} loading={policyLoading} />
    </div>
  );
}
